import { Box, Container } from "@/components/atoms/index";
import Typography from "@mui/material/Typography";

interface ContentPageProps {
  componentName: string;
  pageHeader: string;
  paragraphObject: {
    header?: string;
    paragraph?: string;
    links?: string[];
  }[];
}

export const ContentPage = ({
  componentName,
  pageHeader,
  paragraphObject,
}: ContentPageProps) => {
  const renderParagraph = (paragraph: string) => {
    const lines = paragraph.split("\n");
    const hasBulletPoints = lines.some(line => line.trim().startsWith("-"));

    if (hasBulletPoints) {
      const textBeforeList = lines
        .filter(line => !line.trim().startsWith("-"))
        .join("\n");
      const bulletPoints = lines.filter(line => line.trim().startsWith("-"));

      return (
        <div>
          {textBeforeList && (
            <Typography
              data-testid={`${componentName}-content-page-paragraph`}
              variant="body1"
              align="left"
              color="primary.main"
              sx={{
                my: 1,
                lineHeight: 2,
                letterSpacing: 1,
                wordSpacing: 1,
              }}
            >
              {textBeforeList}
            </Typography>
          )}

          <ul>
            {bulletPoints.map((line, index) => (
              <li key={index}>
                <Typography
                  variant="body1"
                  color="primary.main"
                  sx={{
                    my: 1,
                    lineHeight: 2,
                    letterSpacing: 1,
                    wordSpacing: 1,
                  }}
                >
                  {line.trim().replace(/^-\s*/, "")}
                </Typography>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <Typography
        data-testid={`${componentName}-content-page-paragraph`}
        variant="body1"
        align="left"
        color="primary.main"
        sx={{
          my: 1,
          lineHeight: 2,
          letterSpacing: 1,
          wordSpacing: 1,
        }}
      >
        {paragraph}
      </Typography>
    );
  };

  return (
    <Container
      componentName={`${componentName}-content-page`}
      sx={{
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <Box
        componentName={`${componentName}-content-page-parent-box`}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          m: 2,
        }}
      >
        <Box
          componentName={`${componentName}-content-page-box`}
          border={false}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            p: 4,
            my: 2,
            mx: 10,
          }}
        >
          <Typography
            variant="h3"
            align="center"
            color="primary.dark"
            sx={{
              my: 1,
              lineHeight: 2,
              textTransform: "capitalize",
              letterSpacing: 2,
              wordSpacing: 2,
            }}
          >
            {pageHeader}
          </Typography>
          {paragraphObject.map((item, index) => (
            <Box
              componentName={`${componentName}-content-page-box-${index}`}
              key={index}
              border={false}
            >
              {item.header && (
                <Typography
                  data-testid={`${componentName}-content-page-box-${index}-header`}
                  variant="h5"
                  align="center"
                  color="primary.main"
                  sx={{
                    my: 1,
                    lineHeight: 2,
                    textTransform: "capitalize",
                    letterSpacing: 1,
                    wordSpacing: 1,
                  }}
                >
                  {item.header}
                </Typography>
              )}
              {item.paragraph && renderParagraph(item.paragraph)}{" "}
              {item.links && item.links.length > 0 && (
                <Box
                  componentName={`${componentName}-content-page-box-${index}-links`}
                  border={false}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    my: 2,
                  }}
                >
                  {item.links.map((link, linkIndex) => (
                    <Typography
                      key={`link-${linkIndex}`}
                      align="center"
                      component="a"
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="primary.main"
                      sx={{
                        my: 1,
                        lineHeight: 2,
                        letterSpacing: 1,
                        wordSpacing: 1,
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      {link}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
};
